import { useEffect, useMemo, useState } from "react";
import { useTheme } from "@emotion/react";
import { BannerPage } from "../../uiLibrary/bannerPage/BannerPage";
import { Spinner } from "../../uiLibrary/spinner/Spinner";
import { Warning } from "../../uiLibrary/icons/Warning";
import { sanitizeEditorHtml } from "../../uiLibrary/editor/sanitizeEditorHtml";
import { ensureDate, formatShortDateOnly } from "../../other/dateTime/format";
import { useSingleton } from "../../other/injection";
import { MessageBusService } from "../../other/messageBus/MessageBusService";
import { locationPrepend } from "../../other/devServer/locationPrepend";
import { InternalMediaListLink } from "./InternalMediaListLink";

type InternalMediaDto =
    RedDeer.InternalMedia.Client.UI.Contracts.Dtos.InternalMediaDto;

const INTERNAL_MEDIA_RESOURCE_SRC =
    /(src\s*=\s*["'])(\/InternalMedia\/Resource\/)/gi;

function withInternalMediaResourceOrigin(html: string): string {
    const origin = locationPrepend();
    if (!origin) {
        return html;
    }

    return html.replace(INTERNAL_MEDIA_RESOURCE_SRC, `$1${origin}$2`);
}

export function InternalMediaArticlePage({ id }: { id: string }) {
    const theme = useTheme();
    const isDark = theme.name === "dark";
    const messageBusService = useSingleton(MessageBusService);

    const [media, setMedia] = useState<InternalMediaDto | undefined>();
    const [hasResult, setHasResult] = useState(false);
    const [error, setError] = useState<string | undefined>();

    const sanitizedBody = useMemo(
        () =>
            withInternalMediaResourceOrigin(
                sanitizeEditorHtml(media?.body ?? "")
            ),
        [media?.body]
    );

    useEffect(() => {
        if (!id) {
            setError("Article not found");
            setHasResult(true);
            return;
        }

        const request: RedDeer.InternalMedia.Client.UI.Contracts.Queries.GetInternalMediaQuery =
            {
                MessageType: "Queries.GetInternalMediaQuery",
                Assembly: "RedDeer.InternalMedia.Client.UI.Contracts",
                id: id
            };

        void messageBusService
            .asyncSendPromise<RedDeer.InternalMedia.Client.UI.Contracts.Queries.GetInternalMediaResponse>(
                {
                    description: "Fetching internal media article",
                    request
                }
            )
            .then(response => {
                if (!response.internalMedia) {
                    setError("Article not found");
                } else {
                    setMedia(response.internalMedia);
                }
            })
            .catch(() => {
                setError("Failed to load article");
            })
            .finally(() => {
                setHasResult(true);
            });
    }, [id, messageBusService]);

    if (!hasResult) {
        return (
            <BannerPage title="Community" slim>
                <div className="w-full py-12 text-center">
                    <Spinner />
                </div>
            </BannerPage>
        );
    }

    if (error || !media) {
        return (
            <BannerPage title="Community" slim>
                <div className="w-full py-12 text-center">
                    <Warning size={50} />
                    <div className="mt-4">{error ?? "Article not found"}</div>
                </div>
            </BannerPage>
        );
    }

    return (
        <BannerPage slim>
            <div className="w-full pt-[30px]">
                <div className="mb-6">
                    <InternalMediaListLink />
                </div>
                <article
                    className={`w-full rounded-xl border p-6 shadow-sm ${
                        isDark
                            ? "border-slate-600 bg-slate-800"
                            : "border-slate-200 bg-white"
                    }`}
                >
                    {media.headerImage && (
                        <img
                            src={`${locationPrepend()}/InternalMedia/Resource/${media.headerImage}`}
                            alt={media.title ?? ""}
                            className="mb-6 w-full rounded-lg"
                        />
                    )}
                    <div
                        className={`mb-2 text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}
                    >
                        {media.title}
                    </div>
                    <div className="mb-4 flex flex-wrap items-center gap-3">
                        {media.author && (
                            <span
                                className={`text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}
                            >
                                {media.author}
                            </span>
                        )}
                        <span
                            className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}
                        >
                            {formatShortDateOnly(
                                ensureDate(media.publishedDateTime)
                            )}
                        </span>
                    </div>

                    <div
                        className={`text-lg leading-relaxed [&_img]:max-w-full [&_img]:rounded-lg ${isDark ? "text-slate-300" : "text-slate-600"}`}
                        dangerouslySetInnerHTML={{ __html: sanitizedBody }}
                    />
                </article>
            </div>
        </BannerPage>
    );
}
